import React from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Fade
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  SignalWifiOff as SignalWifiOffIcon
} from '@mui/icons-material';

const ConnectionBanner = ({ 
  connectionStatus, 
  onRetry,
  sx = {} 
}) => {
  // Ensure connectionStatus is a valid object
  if (!connectionStatus || typeof connectionStatus !== 'object' || connectionStatus.message) {
    return null;
  }

  const { isConnected, isReconnecting, reconnectAttempts, maxReconnectAttempts } = connectionStatus;
  
  // Don't show banner if connected
  if (isConnected) {
    return null;
  }

  // Show reconnecting banner (WhatsApp style)
  if (isReconnecting) {
    return (
      <Fade in={true}>
        <Box
          sx={{
            backgroundColor: '#fef3c7',
            border: 'none',
            borderBottom: '1px solid #f59e0b',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            marginBottom: 0,
            borderRadius: 0,
            minHeight: '40px',
            ...sx
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={16} sx={{ color: '#92400e' }} />
            <Typography variant="body2" sx={{ color: '#92400e', fontSize: '14px', fontWeight: 'normal' }}>
              Reconnecting...
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#92400e', fontSize: '12px', fontWeight: 'medium' }}>
            {reconnectAttempts}/{maxReconnectAttempts}
          </Typography>
        </Box>
      </Fade>
    );
  }

  // Show connection failed banner (WhatsApp style) - also show when offline
  // Only show when NOT reconnecting AND (max attempts reached OR offline)
  if (!isConnected && !isReconnecting && (reconnectAttempts >= maxReconnectAttempts || !navigator.onLine)) {
    const isOffline = !navigator.onLine;
    
    return (
      <Fade in={true}>
        <Box
          sx={{
            backgroundColor: '#fef2f2',
            border: 'none',
            borderBottom: '1px solid #fca5a5',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 0,
            borderRadius: 0,
            minHeight: '40px',
            ...sx
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <SignalWifiOffIcon sx={{ color: '#dc2626', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '14px', fontWeight: 'normal' }}>
              {isOffline ? 'No internet connection' : 'You are away, reconnect'}
            </Typography>
          </Box>
          <Button
            variant="text"
            size="small"
            onClick={onRetry}
            startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: '#25D366',
              fontSize: '14px',
              fontWeight: 'normal',
              textTransform: 'none',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#128C7E'
              }
            }}
          >
            Reconnect
          </Button>
        </Box>
      </Fade>
    );
  }

  return null;
};

export default ConnectionBanner;
